/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderApplicationService,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  CommonFlowState,
  FormStateBuilder,
  ListActionPipelines,
  UnpaidInvoicesDialogComponent,
  UnpaidInvoicesDialogData,
} from '@gh/builder-shared';
import { OfficeStatus } from '@gh/builder-shared-data';
import {
  AppRouter,
  filterableForm,
  FilterableFormState,
  getRouteInitData,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { cursorTracker, newGridPaginationCursor } from '@gh/core-ui-grid';

import { OfficeListBusinessService } from '../../resources/office-list-business.service';
import { OfficeListItem } from '../../resources/office-list-item.model';
import { OfficeListGridDefinition } from './office-list-grid-definition.service';
import { Confirmation, DialogService, NotificationService, WaitingService } from '@gh/core-ui';

@Component({
  standalone: false,
  selector: 'gh-office-list',
  templateUrl: 'office-list.component.html',
  styleUrls: ['office-list.component.scss'],
  viewProviders: [OfficeListGridDefinition],
})
export class OfficeListComponent implements OnInit {
  offices: SlicedDataSource<OfficeListItem>;
  dataSource: SlicedDataSource<OfficeListItem>;
  OfficeStatus = OfficeStatus;
  cursor = newGridPaginationCursor();
  form: FilterableFormState<any>;
  filter: any;

  get canCreate(): boolean {
    return this.userContext.masterView;
  }

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private routeData: RouteDataService,
              private userContext: BuilderUserContext,
              private navigationService: BuilderNavigationService,
              private applicationService: BuilderApplicationService,
              private businessService: OfficeListBusinessService,
              private pipelines: ListActionPipelines,
              private commonActions: CommonActions,
              private formStateBuilder: FormStateBuilder,
              private notificationService: NotificationService,
              private dialogService: DialogService,
              private waitings: WaitingService,
              public gridDefinition: OfficeListGridDefinition) {
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    const { gridLayout } = getRouteInitData(this.route);
    this.gridDefinition.bind(gridLayout);
    this.form = filterableForm(this.formStateBuilder.buildPlain(
      {
        value:    {},
        children: {
          name:  void 0,
          showDeactivated:  void 0,
        },
      }));
    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      this.filter = {
        ...filter,
      };
      this.dataSource = this.businessService.findAllAsDataSource(this.filter);
    });
  }

  reload(): void {
    this.loadData();
  }

  loadData(): void {
    this.dataSource = this.businessService.findAllAsDataSource(this.filter);
  }

  onCreate(): void {
    this.router.navigateToChild(['/setup/offices/new']);
  }

  onEdit(office: OfficeListItem): void {
    if (office && office.statusValue === OfficeStatus.InDeletion) {
      return;
    }
    if (!this.isOfficeDataImport(office)) {
      this.router.navigateToChild(['/setup/offices', office.officeId]);
    }
  }

  onDeactivate(office: OfficeListItem): void {
    this.dialogService.confirmation({ messageId: 'msg_office_deactivation' }).subscribe((result) => {
      if (result === Confirmation.Yes) {
        this.executeDeactivate(office);
      }
    });
  }

  private executeDeactivate(office: OfficeListItem, force: boolean = false): void {
    this.businessService.deactivate(office, force).pipe(this.waitings.operator()).subscribe((result) => {
      if (result.unpaidInvoices && result.unpaidInvoices.length > 0) {
        const input: UnpaidInvoicesDialogData = {
          unpaidInvoices: result.unpaidInvoices!,
          productName: office.name,
          customMessage: 'This office has unpaid invoices from active subscriptions. Please review them before proceeding with deactivation.',
          showProceedButton: true
        };
        const dialogRef = this.dialogService.openWithInput(UnpaidInvoicesDialogComponent, input, { disableClose: true });

        dialogRef.afterClosed().subscribe((acknowledged) => {
          if (acknowledged) {
            this.executeDeactivate(office, true);
          }
        });
      } else {
        this.notificationService.success('msg_office_deactivated_successfully', { name: office.name });
        this.loadData();
      }
    }, this.notificationService.processAll);
  }

  isOfficeDataImport(office: OfficeListItem): boolean {
    return office.isDataImport;
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'OFFICE',
      label: label ? label : 'lbl_offices_list',
    });
  }

  onDelete(office: OfficeListItem): void {
    const flow = this.pipelines.toggleItem({
      question: 'msg_office_deletion',
      successMessage: { id: 'msg_office_deleted_successfully', params: { name: office.name } },
      execute: this.commonActions.execute((state: CommonFlowState<OfficeListItem>) =>
        this.businessService.delete(state.value)),
      finish: () => this.loadData(),
    });

    flow.run({
      value: office,
    });
  }

  onSwitch(office: OfficeListItem): void {
    this.applicationService.openOfficeView(office.officeId);
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  validate(office: OfficeListItem): void {
    this.businessService.validate(office.officeId).subscribe(() => {
      this.notificationService.success('msg_validate_office_google_folders_task_added', {officeName: office.name});
    },                                                       (error) => {
      this.notificationService.processAll(error);
    });
  }
}
